<template>
  <div class="button-group" :style="{ flexDirection: buttonFlowStyle }">
    <div class="dynamic-container half">
      <transition name="button-fade">
        <component
          :is="actionComponent(visibleAction)"
          class="dynamic base-button"
          :class="dynamicSideClass"
          :style="actionStyle(visibleAction)"
          v-bind="actionAttrs(visibleAction)"
          :key="visibleAction"
        >
          <div>{{ actionStringKey(visibleAction, "title") }}</div>
          <img
            v-if="isValidUrl(actionStringKey(visibleAction, 'image'))"
            :src="actionStringKey(visibleAction, 'image')"
            class="icon"
          />
          <div
            v-else
            v-html="actionStringKey(visibleAction, 'image')"
            class="icon"
          ></div>
        </component>
      </transition>
    </div>
    <component
      :is="actionComponent(staticAction)"
      v-bind="actionAttrs(staticAction)"
      class="base-button"
      :class="staticSideClass"
      :style="actionStyle(staticAction)"
    >
      <div>{{ actionStringKey(staticAction, "title") }}</div>
      <img
        v-if="isValidUrl(actionStringKey(staticAction, 'image'))"
        :src="actionStringKey(staticAction, 'image')"
        class="icon"
      />
      <div
        v-else
        v-html="actionStringKey(staticAction, 'image')"
        class="icon"
      ></div>
    </component>
  </div>
</template>
   

<script>
export default {
  props: {
    slideInterval: {
      type: Number,
      default: 5000,
    },
    dynamicActions: {
      type: Array,
      required: true,
    },
    staticAction: {
      type: Object,
      required: true,
    },
    dynamicSide: {
      type: String,
      default: "left",
    },
    randomized: {
      type: Boolean,
      default: false,
    },
    trackEventTag: {
      type: String,
      default: null,
    },
  },
  inject: ["trackEvent"],
  data() {
    return {
      elapsed: 0,
      timerId: null,
      currentSlide: 0,
      orderedActions: [],
    };
  },
  computed: {
    dynamicActionCount() {
      return this.dynamicActions.length;
    },
    dynamicSideClass() {
      return this.dynamicSide === "left" ? "left" : "right";
    },
    staticSideClass() {
      return {
        left:
          this.dynamicSide === "right" &&
          Object.keys(this.visibleAction ?? {}).length !== 0,
        right:
          this.dynamicSide === "left" &&
          Object.keys(this.visibleAction ?? {}).length !== 0,
        half: Object.keys(this.visibleAction ?? {}).length !== 0,
        "single-button": Object.keys(this.visibleAction ?? {}).length === 0,
      };
    },
    buttonFlowStyle() {
      return this.dynamicSide === "left" ? "row" : "row-reverse";
    },
    visibleAction() {
      if (this.dynamicActionCount === 0) {
        return {};
      }
      return this.orderedActions[this.currentSlide];
    },
  },
  methods: {
    getContrastingTextColor(bgColor) {
      // Remove '#' if present
      if (bgColor.startsWith("#")) {
        bgColor = bgColor.slice(1);
      }

      // Convert 3-digit hex to 6-digit hex
      if (bgColor.length === 3) {
        bgColor = bgColor
          .split("")
          .map((c) => c + c)
          .join("");
      }

      const r = parseInt(bgColor.substr(0, 2), 16);
      const g = parseInt(bgColor.substr(2, 2), 16);
      const b = parseInt(bgColor.substr(4, 2), 16);

      // Perceived luminance (https://en.wikipedia.org/wiki/Relative_luminance)
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

      return luminance > 150 ? "#333" : "#ddd"; // Threshold can be adjusted
    },
    isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    },
    actionStringKey(action, key) {
      if (action == null) {
        return "";
      }
      return action?.[key] || "";
    },
    actionStyle(action) {
      if (action == null) {
        return {};
      }
      if ("color" in action?.style) {
        return action.style;
      }
      return {
        ...action.style,
        color: this.getContrastingTextColor(action.style.backgroundColor),
      };
    },
    actionComponent(action) {
      if (action?.url != null) {
        return "a";
      } else {
        return "div";
      }
    },
    actionAttrs(action) {
      if (action?.url != null) {
        return {
          href: action.url,
          target: "_blank",
          onClick: () => this.actionTaken(action),
        };
      } else if (action?.modal != null) {
        return { onClick: () => this.setModal(action.modal) };
      }
      return {};
    },
    setModal(parameters) {
      this.$emit("set-modal", ...parameters);
    },
    actionTaken(action) {
      if (this.trackEventTag != null) {
        this.trackEvent(this.trackEventTag, {
          title: action?.title,
          mode: "multi-action",
        });
      }
    },
    shuffleActions(actionArray) {
      const copied = [...actionArray];
      for (let i = copied.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copied[i], copied[j]] = [copied[j], copied[i]];
      }
      return copied;
    },
  },
  mounted() {
    this.intervalId = setInterval(() => {
      this.currentSlide = (this.currentSlide + 1) % this.dynamicActionCount;
    }, this.slideInterval);
  },
  watch: {
    dynamicActions: {
      immediate: true,
      handler(newActions) {
        if (newActions && newActions.length) {
          if (this.randomized) {
            this.orderedActions = this.shuffleActions(newActions);
          } else {
            this.orderedActions = [...newActions];
          }
        }
      },
    },
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
};
</script>

<style scoped>
.action-title {
  overflow: hidden;
}
.half {
  width: 50%;
  font-size: 0.85em;
}
.base-button {
  display: flex;
  border: 0;
  height: 6.25vh;
  padding: 0;
  text-decoration: none;
  justify-content: center;
  align-items: center;
  border-radius: 3.4vh;
  font-family: "Fira Sans", system-ui;
  overflow: hidden;
  cursor: pointer;
  gap: 1vh;
  -webkit-justify-content: center;
}

.base-button .icon {
  height: 2.5vh;
}

.base-button div {
  color: inherit;
}

.left {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.right {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  flex-direction: row-reverse;
}
.middle {
  border-radius: 0;
  padding: 0 1vh;
}

.button {
  border-radius: 3.4vh;
}

.button-fade-enter-active,
.button-fade-leave-active {
  transition: transform 0.25s ease, opacity 0.5s ease;
}

.button-fade-leave-to {
  opacity: 0;
  transform: translateY(1vh);
}

.button-fade-enter-from {
  opacity: 0;
  transform: translateY(-1vh);
}
.dynamic-container {
  display: flex;
  position: relative;
  /* width: 100%; */
}

.dynamic {
  position: absolute;
  width: 100%;
}
</style>
